import React, { useCallback } from 'react';
import { Control, FieldErrors, UseFieldArrayAppend, UseFieldArrayRemove, useWatch } from 'react-hook-form';

import { Button, DynamicIconWrapper, Icons, Meta, Select, SpinnerOverlay } from '@components/common';

import {
	useQueryAlarmsNoVesNetworkElements,
} from '@hooks/alarms/alarmManagamentData/useQueryAlarmsNoVesNetworkElements';

import { IAlarmManagement } from '@interfaces/alarms/alarm';
import { TSelectOption } from '@interfaces/components/componentTypes';

import { EAlarmManagementFields } from '../enums';

import { nonNullishMinutesOptions } from '@constants/timeOptions';

import { IFieldsAlarmManagementForm, INetworkElementFields } from '../AlarmManagementForm';

import timeStyles from '../Schedule/Schedule.module.scss';
import styles from './CustomAlarm.module.scss';

interface ICustomAlarmProps {
	networkElementFields: INetworkElementFields[];
	editableAlarm?: IAlarmManagement;
	control: Control;
	errors: FieldErrors<IFieldsAlarmManagementForm>;
	append: UseFieldArrayAppend<any, string>;
	remove: UseFieldArrayRemove;
}

const OPTIONS_MAX_HEIGHT = 120;

const CustomAlarm = ({
	networkElementFields,
	editableAlarm,
	control,
	errors,
	append,
	remove,
}: ICustomAlarmProps) => {
	const addNewAlarmHandler = useCallback(() => {
		append({ ruleId: undefined, networkElement: null, minutes: nonNullishMinutesOptions[0] });
	}, [append]);

	const {
		noVesNetworkElementsOptions,
		isLoadingAlarmsNoVesNetworkElements,
	} = useQueryAlarmsNoVesNetworkElements();

	const watchedFields: INetworkElementFields[] = useWatch({
		name: EAlarmManagementFields.networkElementFields,
		control,
	});

	const controlledFields: INetworkElementFields[] = networkElementFields.map((field, index) => {
		return {
			...field,
			...watchedFields[index],
		};
	});

	const editableAlarmOptions: TSelectOption[] = editableAlarm
		?.customSettings
		.noVesEventAlarmCreateSettings
		?.filter(Boolean)
		.map((item) => ({
			label: item.networkElementName,
			value: item.networkElementId,
		})) || [];

	const filteredNetworkElementsOptions = [...noVesNetworkElementsOptions, ...editableAlarmOptions]
		.filter((option) => !controlledFields.find((rule) => rule.networkElement?.value === option?.value));

	return (
		<div className={styles.wrapper}>
			<Button
				className={styles.button}
				icon={Icons.plus()}
				onClick={addNewAlarmHandler}
				secondary
				text='Add new rule'
			/>
			<div className={styles.rulesList}>
				{controlledFields.map((alarm, index: number) => {
					const alarmName = `${EAlarmManagementFields.networkElementFields}[${index}]`;

					const networkElementsError = errors
						?.networkElementFields?.[index]?.networkElement?.message;

					const deleteRuleHandler = () => {
						remove(index);
					};

					return (
						<div
							key={alarm.id}
							className={styles.alarm}
						>
							{isLoadingAlarmsNoVesNetworkElements && <SpinnerOverlay />}
							<span className={styles.close}>
								<DynamicIconWrapper
									icon={Icons.close}
									onClick={deleteRuleHandler}
								/>
							</span>
							<div className={styles.networkSelect}>
								<Select
									control={control}
									isError={!!networkElementsError}
									label='Network Element'
									maxMenuHeight={OPTIONS_MAX_HEIGHT}
									menuPlacement='top'
									name={`${alarmName}.networkElement`}
									options={filteredNetworkElementsOptions}
									required
								/>
								{!!networkElementsError && <Meta message={networkElementsError} />}
							</div>
							<div className={timeStyles.scheduleContainer}>
								<div className={timeStyles.timeSection}>
									<div>Time interval after which no PM ves event was received:</div>
									<div className={timeStyles.timeSelect}>
										<Select
											control={control}
											maxMenuHeight={OPTIONS_MAX_HEIGHT}
											menuPlacement='top'
											name={`${alarmName}.minutes`}
											options={nonNullishMinutesOptions}
											placeholder='mm'
											required
										/>
									</div>
									<div>min</div>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default CustomAlarm;
